
import { useState, useEffect, useCallback } from 'react';
import { cryptoDataService } from '@/services/cryptoDataService';
import type { Database } from '@/integrations/supabase/types';

type CryptoToken = Database['public']['Tables']['crypto_tokens']['Row'];

interface UseCryptoDataOptions {
  page?: number;
  limit?: number;
  sortBy?: 'market_cap_rank' | 'market_cap' | 'price_change_percentage_24h' | 'total_volume';
  sortDirection?: 'asc' | 'desc';
  category?: string;
  enableRealtime?: boolean;
}

export const useCryptoData = (options: UseCryptoDataOptions = {}) => {
  const {
    page = 1,
    limit = 100,
    sortBy = 'market_cap_rank',
    sortDirection = 'asc',
    category,
    enableRealtime = true
  } = options;

  const [tokens, setTokens] = useState<CryptoToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [realtimeStatus, setRealtimeStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connecting');
  const [prevPrices, setPrevPrices] = useState<Record<string, number>>({});

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await cryptoDataService.getTokens(
        page,
        limit,
        sortBy,
        sortDirection,
        category
      );

      if (result.error) {
        setError(result.error);
      } else {
        // Track previous prices before updating
        const newPrevPrices: Record<string, number> = {};
        result.data.forEach(token => {
          if (token.current_price) {
            newPrevPrices[token.id] = token.current_price;
          }
        });
        setPrevPrices(newPrevPrices);
        
        setTokens(result.data);
        setTotal(result.total);
        setLastUpdate(new Date());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tokens');
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortBy, sortDirection, category]);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  useEffect(() => {
    if (!enableRealtime) return;

    setRealtimeStatus('connecting');
    
    const channel = cryptoDataService.subscribeToTokenUpdates((payload) => {
      if (payload.eventType === 'UPDATE') {
        setRealtimeStatus('connected');
        setTokens(prev => prev.map(token => {
          if (token.id === payload.new.id) {
            // Track previous price before updating
            if (token.current_price) {
              setPrevPrices(prevPrices => ({
                ...prevPrices,
                [token.id]: token.current_price || 0
              }));
            }
            setLastUpdate(new Date());
            return payload.new;
          }
          return token;
        }));
      }
    });

    // Simulate connection check
    const connectionCheckTimer = setTimeout(() => {
      if (channel) {
        setRealtimeStatus('connected');
      }
    }, 1000);

    return () => {
      clearTimeout(connectionCheckTimer);
      setRealtimeStatus('disconnected');
      cryptoDataService.unsubscribeFromTokenUpdates();
    };
  }, [enableRealtime]);

  const refreshTokens = useCallback(() => {
    fetchTokens();
  }, [fetchTokens]);

  return {
    tokens,
    loading,
    error,
    total,
    refreshTokens,
    lastUpdate,
    realtimeStatus,
    prevPrices
  };
};

export const useTokenSearch = () => {
  const [searchResults, setSearchResults] = useState<CryptoToken[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const searchTokens = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await cryptoDataService.searchTokens(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  return {
    searchResults,
    isSearching,
    searchTokens
  };
};

export const useTrendingTokens = (limit: number = 10) => {
  const [trendingTokens, setTrendingTokens] = useState<CryptoToken[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrending = async () => {
      setLoading(true);
      try {
        const tokens = await cryptoDataService.getTrendingTokens(limit);
        setTrendingTokens(tokens);
      } catch (error) {
        console.error('Error fetching trending tokens:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrending();
  }, [limit]);

  return { trendingTokens, loading };
};
